import { useMemo } from 'react';
import type { BacklightReport, LedLayout, LedMounting } from '@/core/types';
import { CONSTRAINTS } from '@/core/design/constraints';
import { LED_TYPES, PANEL_MATERIALS, ledType, panelMaterial } from '@/core/led/light';
import { useDesignStore } from '@/state/store';
import { Section, Segmented, Select, Slider, Toggle } from '../components/Controls';

/**
 * Backlighting controls.
 *
 * The air gap is the headline, because it is the number that decides whether
 * the wall glows or shows a row of dots — and it is the one the previous
 * version of this feature had no way to express at all. Everything here reports
 * against the three rules that matter, with the value that would satisfy them,
 * rather than leaving someone to find out after a print.
 */

const MOUNTINGS = [
  {
    value: 'channel',
    label: 'In the panel',
    help: 'A groove in the panel\u2019s own back. One part per tile, hanging flat.',
  },
  {
    value: 'lightbox',
    label: 'Light box',
    help: 'A separate frame holding the strip back from the panel. Two parts per tile, even light.',
  },
] as const;

const LAYOUTS = [
  { value: 'horizontal', label: 'Rows', help: 'Runs across the wall.' },
  { value: 'vertical', label: 'Columns', help: 'Runs up the wall.' },
  { value: 'grid', label: 'Grid', help: 'Both directions, for the most even light.' },
  { value: 'perimeter', label: 'Outline', help: 'A loop inside each box.' },
] as const;

export function LedSection({ report }: { report: BacklightReport | null }) {
  const design = useDesignStore((state) => state.design);
  const setLed = useDesignStore((state) => state.setLed);

  const { led, panel } = design;
  const type = useMemo(() => ledType(led.ledType), [led.ledType]);
  const material = useMemo(() => panelMaterial(led.panelMaterial), [led.panelMaterial]);

  return (
    <Section
      title="Backlighting"
      description="Routes an LED strip behind the relief — either in a groove in the panel itself, or in a separate box that gives the light room to spread."
    >
      <Toggle
        label="Backlight this wall"
        checked={led.enabled}
        onChange={(enabled) => setLed({ enabled })}
      />

      {led.enabled ? (
        <>
          <Segmented<LedMounting>
            label="Mounting"
            value={led.mounting}
            options={MOUNTINGS}
            onChange={(mounting) => setLed({ mounting })}
          />

          {led.mounting === 'channel' ? (
            <Slider
              label="Tape seat"
              value={led.channelDepth}
              min={CONSTRAINTS.channelDepth.min}
              max={CONSTRAINTS.channelDepth.max}
              step={CONSTRAINTS.channelDepth.step}
              unit="mm"
              help="Seat for the tape. A deeper pocket for the emitters is added under it from the strip's own package height, so the groove ends up deeper than this."
              onChange={(channelDepth) => setLed({ channelDepth })}
            />
          ) : (
            <Slider
              label="Air gap"
              value={led.airGap}
              min={CONSTRAINTS.airGap.min}
              max={CONSTRAINTS.airGap.max}
              step={CONSTRAINTS.airGap.step}
              unit="mm"
              help="Distance from the strip to the back of the panel. This is what makes individual LEDs blend into a line."
              onChange={(airGap) => setLed({ airGap })}
            />
          )}

          <Select
            label="Strip"
            value={led.ledType}
            options={LED_TYPES.map((entry) => ({ value: entry.id, label: entry.label }))}
            help={`Emitters every ${type.pitchMm.toFixed(1)} mm, about ${type.wattsPerMetre} W per metre at full white.`}
            onChange={(ledType) => setLed({ ledType })}
          />

          <Select
            label="Panel filament"
            value={led.panelMaterial}
            options={PANEL_MATERIALS.map((entry) => ({ value: entry.id, label: entry.label }))}
            help="Light has to cross the panel itself. This is the one choice made at the printer, not here."
            onChange={(panelMaterial) => setLed({ panelMaterial })}
          />

          {report ? <LightVerdict report={report} /> : null}

          {material.opaque ? (
            <div className="notice notice--warn">
              <p>
                An opaque filament stops the light. Print the panels in a white or translucent
                material, or the frames will glow and the wall will not.
              </p>
            </div>
          ) : null}

          <Segmented<LedLayout>
            label="Routing"
            value={led.layout}
            options={LAYOUTS}
            onChange={(layout) => setLed({ layout })}
          />

          {led.layout === 'perimeter' ? (
            <Slider
              label="Inset from the box wall"
              value={led.inset}
              min={CONSTRAINTS.ledInset.min}
              max={Math.min(CONSTRAINTS.ledInset.max, panel.size / 3)}
              step={CONSTRAINTS.ledInset.step}
              unit="mm"
              onChange={(inset) => setLed({ inset })}
            />
          ) : (
            <>
              <Slider
                label="Run spacing"
                value={led.spacing}
                min={0.25}
                max={4}
                step={0.25}
                unit="panels"
                help={`One run every ${(led.spacing * (panel.size + panel.gap)).toFixed(0)} mm. Measured in panels, so runs keep meeting the joints.`}
                onChange={(spacing) => setLed({ spacing })}
              />
              <Slider
                label="Run offset"
                value={led.offset}
                min={0}
                max={1}
                step={0.01}
                help="Shifts the runs across the wall. At 0.5 they sit in the middle of each panel."
                onChange={(offset) => setLed({ offset })}
              />
            </>
          )}

          <Slider
            label="Strip width"
            value={led.stripWidth}
            min={CONSTRAINTS.stripWidth.min}
            max={CONSTRAINTS.stripWidth.max}
            step={CONSTRAINTS.stripWidth.step}
            unit="mm"
            help={
              led.mounting === 'channel'
                ? 'Sets how wide the groove is cut. Measure the strip including its sleeve.'
                : 'Sets how wide the slots between boxes are cut.'
            }
            onChange={(stripWidth) => setLed({ stripWidth })}
          />

          <Toggle
            label="Continuous between boxes"
            checked={led.passThrough}
            help="Lets a run reach the panel edge, so one strip serves several tiles. Turn it off for a self-contained strip per tile."
            onChange={(passThrough) => setLed({ passThrough })}
          />

          {led.mounting === 'lightbox' ? (
            <>
              <Slider
                label="Box wall"
                value={led.frameWall}
                min={CONSTRAINTS.frameWall.min}
                max={CONSTRAINTS.frameWall.max}
                step={CONSTRAINTS.frameWall.step}
                unit="mm"
                onChange={(frameWall) => setLed({ frameWall })}
              />
              <Slider
                label="Box floor"
                value={led.frameFloor}
                min={CONSTRAINTS.frameFloor.min}
                max={CONSTRAINTS.frameFloor.max}
                step={CONSTRAINTS.frameFloor.step}
                unit="mm"
                help="The strip sits on this. White filament here reflects light forward."
                onChange={(frameFloor) => setLed({ frameFloor })}
              />
            </>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}

/**
 * The three optical checks, stated as verdicts.
 *
 * All three are rules of thumb rather than a simulation, so each one says what
 * the design has and what it would need — a number someone can act on beats a
 * red or green dot they have to interpret.
 *
 * The transmission line used to be the exception: two bare percentages, from
 * which "1% at the deepest relief" had to be translated into "the peaks stay
 * black" by the reader. It says that outright now, and names the relief depth
 * that would change it.
 */
function LightVerdict({ report }: { report: BacklightReport }) {
  const percent = (value: number): string => `${Math.round(value * 100)}%`;
  const light = report.transmission;
  const ok = report.blending.ok && report.uniformity.ok && light.ok;

  return (
    <div className={`notice${ok ? '' : ' notice--warn'}`}>
      {report.channel ? (
        <p>
          {report.channel.seatMm < report.channel.requestedMm - 0.05 ? (
            <>
              <strong>The tape seat is only {report.channel.seatMm.toFixed(1)} mm.</strong> The
              groove has to stay inside the backing plate, and the{' '}
              {report.channel.emitterMm.toFixed(1)} mm pocket the emitters stand in comes first. A{' '}
              {report.channel.plateForRequestMm.toFixed(1)} mm plate would give the{' '}
              {report.channel.requestedMm} mm asked for.
            </>
          ) : (
            <>
              A {report.channel.seatMm.toFixed(1)} mm seat for the tape, with a{' '}
              {report.channel.emitterMm.toFixed(1)} mm pocket{' '}
              {report.channel.emitterWidthMm.toFixed(1)} mm wide under it for the emitters —{' '}
              {report.channel.depthMm.toFixed(1)} mm at its deepest, leaving{' '}
              {report.channel.diffuserMm.toFixed(1)} mm of panel over the light.
            </>
          )}
        </p>
      ) : null}
      <p>
        {report.blending.ok ? (
          report.mounting === 'channel' ? (
            <>This strip is continuous, so it reads as a line even under the panel.</>
          ) : (
            <>The {report.airGapMm} mm gap blends the emitters into a line.</>
          )
        ) : report.mounting === 'channel' ? (
          <>
            <strong>Individual LEDs will show.</strong> A groove holds them{' '}
            {report.channel?.diffuserMm.toFixed(1)} mm under the surface, and blending needs about{' '}
            {Math.ceil(report.blending.requiredMm)} mm. A COB strip is continuous, or the light-box
            mounting gives them room to mix.
          </>
        ) : (
          <>
            <strong>Individual LEDs will show.</strong> This strip needs about{' '}
            {Math.ceil(report.blending.requiredMm)} mm of gap, or a COB strip.
          </>
        )}
      </p>
      <p>
        {report.mounting === 'channel' ? (
          <>
            Runs {report.uniformity.actualMm.toFixed(0)} mm apart, each lighting a band about as
            wide as the groove. A groove holds the light too close to spread, so the panel glows
            over its runs and stays dark between them.
          </>
        ) : report.uniformity.ok ? (
          <>Runs {report.uniformity.actualMm.toFixed(0)} mm apart light the panel evenly.</>
        ) : (
          <>
            <strong>Bands between the runs.</strong> At this gap they should be no more than{' '}
            {report.uniformity.requiredMm.toFixed(0)} mm apart.
          </>
        )}
      </p>
      <p>
        {!light.ok ? (
          <>
            <strong>Barely any light gets through.</strong> Only {percent(light.brightest)} reaches
            the front where the panel is thinnest. This filament needs a backing plate under{' '}
            {light.maxBaseMm.toFixed(1)} mm, or a clearer one.
          </>
        ) : light.peaksLit ? (
          <>
            The whole surface lights: {percent(light.brightest)} through the backing plate and still{' '}
            {percent(light.darkest)} at the deepest relief.
          </>
        ) : (
          <>
            The relief peaks stay dark — {percent(light.darkest)} gets through them against{' '}
            {percent(light.brightest)} in the valleys, so the pattern reads as a silhouette. Keep
            the relief under {light.maxReliefMm.toFixed(1)} mm for the whole surface to light.
          </>
        )}
      </p>
      <p>
        {report.mounting === 'channel'
          ? `One part per tile, standing ${report.assemblyDepthMm.toFixed(0)} mm off the wall.`
          : `Two parts per tile, standing ${report.assemblyDepthMm.toFixed(0)} mm off the wall.`}
      </p>
    </div>
  );
}
